import React, { useState } from 'react'
import moment from 'moment'
import _ from 'lodash'

import ButtonDropdown, { DropdownButtonItem } from './button-dropdown'
import { PaginationData } from './lost-links-table'
import {
  LostLinksRawData,
  getLostLinksRawData,
} from '../api/apollo/REST/report-client'
import { fixedDates } from '../helpers/report-module'
import useLogAction from '../hooks/useLogAction'
import styles from '../styles/lost-links-action-buttons.module.scss'
import { ListSavedLostLinksReportsQuery } from '../__gql-types__/graphql'

const yesterday = new Date(Date.now())
yesterday.setDate(yesterday.getDate() - 1)

interface LostLinksActionButtonsProps {
  dataConfig?: DataConfig
  getNewReportConfig: () => Partial<SavedReport>
  pagination?: PaginationData | null
  tempTable: string | null
  filtersHaveChanged: boolean
  currentReport: SavedReport | null
  onSaveReport: () => void
  updateSavedReport: any
  savedReports: ListSavedLostLinksReportsQuery['report']['listSavedLinkAuditReports']['savedReports']
  setShowSaveReportModal: React.Dispatch<React.SetStateAction<boolean>>
  setShowEditReportsModal: React.Dispatch<React.SetStateAction<boolean>>
}

export default function LostLinksActionButtons({
  dataConfig,
  getNewReportConfig,
  pagination,
  tempTable,
  filtersHaveChanged,
  currentReport,
  onSaveReport,
  updateSavedReport,
  savedReports,
  setShowSaveReportModal,
  setShowEditReportsModal,
}: LostLinksActionButtonsProps) {
  const logAction = useLogAction()

  const [actionButtonsExpanded, setActionButtonsExpanded] = useState(false)
  const [exporting, setExporting] = useState(false)

  return (
    <div className={styles.actionButtonsWrapper}>
      <ButtonDropdown
        buttonText="Actions"
        containerClassName={styles.bulkButtons}
        separateMainAction={false}
        mainAction={() => {
          if (!actionButtonsExpanded) {
            logAction({
              variables: {
                action: 'action-drop-down-clicked-link-audit',
                extra: '',
                websiteSection: 'report',
                functionName: 'clickDropdown',
                pagePath: window.location.pathname,
              },
            })
          }

          setActionButtonsExpanded(!actionButtonsExpanded)
        }}
      >
        <DropdownButtonItem
          isDisabled={savedReports.length === 0}
          onPress={() => setShowEditReportsModal(true)}
        >
          Manage reports
        </DropdownButtonItem>
        <DropdownButtonItem
          key="save"
          isDisabled={!filtersHaveChanged}
          onPress={async () => {
            if (currentReport === null) {
              setShowSaveReportModal(true)
            } else {
              await updateSavedReport({
                variables: {
                  ...currentReport,
                  ...getNewReportConfig(),
                },
              })

              logAction({
                variables: {
                  action: 'saved-lost-link-reports-update-existing',
                  extra: JSON.stringify({
                    ...currentReport,
                    ...getNewReportConfig(),
                  }),
                  websiteSection: 'report',
                  functionName: 'updateSavedReport',
                  pagePath: window.location.pathname,
                },
              })

              onSaveReport()
            }
          }}
        >
          {currentReport === null ? 'Save report' : 'Save changes'}
        </DropdownButtonItem>
        <DropdownButtonItem key="downloadPDF" isDisabled>
          Download as PDF
        </DropdownButtonItem>
        <DropdownButtonItem
          key="downloadExcel"
          loading={exporting}
          isDisabled={!dataConfig?.startDate || !tempTable}
          onPress={async () => {
            if (!dataConfig || !dataConfig.startDate || !tempTable) return

            const {
              startDate,
              endDate,
              granularity,
              selectedMetric,
              filterList,
            } = dataConfig

            setExporting(true)

            const from =
              fixedDates.indexOf(startDate) > -1
                ? startDate
                : moment(startDate).format('YYYY-MM-DD')

            const rawExcelData: LostLinksRawData = {
              startDate: from,
              granularity: granularity || 'monthly',
              selectedMetric,
              applyFilters: filterList?.dimensionFilters,
              offset: pagination
                ? (pagination.activePage - 1) * pagination.rowsPerPage + 1
                : 1,
              limit: pagination?.rowsPerPage || 25,
              sortDirection: pagination?.sortDirection || 'DESC',
              tempTable,
            }

            if (dataConfig.endDate) {
              rawExcelData.endDate = moment(endDate).format('YYYY-MM-DD')
            }

            await getLostLinksRawData(rawExcelData)

            logAction({
              variables: {
                action: 'download-link-audit-data-excel',
                extra: JSON.stringify(rawExcelData),
                websiteSection: 'report',
                functionName: 'getLostLinksRawData',
                pagePath: window.location.pathname,
              },
            })

            setExporting(false)
          }}
        >
          Download to Excel
        </DropdownButtonItem>
      </ButtonDropdown>
    </div>
  )
}
